/**
 * Elite pricing-details bottom sheet opened from the lock-in price card.
 *
 * Belongs here: Elite-only sheet chrome + slab table from `/v2/portfolio/elite-pricing-details`.
 * Does not belong here: Prime slab API / market holdings — see ElitePlansPricingDetailsBottomSheet.
 */
import { useMemo } from 'react'
import type { ElitePricingDetailsContent } from '../../../types/elitePricingDetails.types'
import { ELITE_PLANS_PRICING_DETAILS_TITLE_ID } from '../../../constants/domIds'
import { resolveElitePricingDetailsTiers } from '../../../elitePlansElitePricingDetailsDisplay'
import { useElitePricingDetails } from '../../../hooks/useElitePricingDetails'
import { ElitePlansEventId, trackElitePlansClick } from '../../../analytics'
import Info from '../../../../../components/ui-components/Info'
import WarningIcon from '../../../../../icons/WarningIcon'
import { getBottomPadding } from '../../../../../utils/sessionStorage'

const GRAPH_ARROW_SRC = '/images/elite-plans/elite/pricing-details/graph-arrow.svg'

type ElitePlansElitePricingDetailsBottomSheetProps = {
  open: boolean
  template: ElitePricingDetailsContent
  /** Formatted fee without suffix, e.g. "₹999". */
  price: string
  /** Live tenure suffix, e.g. "/yr+GST" — overrides CMS when set. */
  priceSuffix: string
  /** Live tenure fee from outside the sheet; highlights via portfolio band, overrides row price on mismatch. */
  feeAmount: number
  onClose: () => void
}

export function ElitePlansElitePricingDetailsBottomSheet({
  open,
  template,
  price,
  priceSuffix: priceSuffixProp,
  feeAmount,
  onClose
}: ElitePlansElitePricingDetailsBottomSheetProps) {
  const { data: pricingDetails, isLoading, isError, refetch } = useElitePricingDetails(open)
  const listPrice = template.listPrice ?? ''

  const { tiers, activeTierId, portfolioValue } = useMemo(
    () =>
      resolveElitePricingDetailsTiers({
        pricingDetails,
        feeAmount,
        listPrice
      }),
    [pricingDetails, feeAmount, listPrice]
  )

  if (isError) {
    return <ElitePricingDetailsErrorState onRetry={() => void refetch()} onClose={onClose} />
  }

  const logoSrc = template.logoSrc ?? ''
  const logoAlt = template.logoAlt ?? ''
  const eyebrow = template.eyebrow ?? ''
  const priceSuffix = priceSuffixProp || template.priceSuffix || ''
  const assuranceBadge = template.assuranceBadge ?? ''
  const portfolioValueLabel = template.portfolioValueLabel ?? ''
  const slabRateLabel = template.slabRateLabel ?? ''
  const slabPortfolioColumnLabel = template.slabPortfolioColumnLabel ?? ''
  const slabPriceColumnLabel = template.slabPriceColumnLabel ?? ''
  const slabFooterNote = template.slabFooterNote ?? ''
  const lockNote = template.lockNote ?? ''

  return (
    <div className='bg-white text-left'>
      <div className="bg-[url('/images/elite-plans/elite-prime/bottomSheet-background.png')] bg-[length:100%_auto] bg-right-top bg-no-repeat px-5 pb-6 pt-8">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={logoAlt}
            width={72}
            height={72}
            className='mb-6 h-[72px] w-[72px] object-contain'
          />
        ) : null}

        <p className='text-[12px] font-medium uppercase tracking-[1px] text-[#7f430e]'>{eyebrow}</p>
        <h2
          id={ELITE_PLANS_PRICING_DETAILS_TITLE_ID}
          tabIndex={-1}
          className='mt-1 text-[24px] font-medium leading-[1.3] text-[#232323] outline-none'
        >
          {price}
          <span className='text-[16px] font-normal'>{priceSuffix}</span>
        </h2>

        {assuranceBadge ? (
          <p className='mt-3 inline-flex rounded-lg bg-gradient-to-r from-[rgba(244,218,198,0.5)] to-[rgba(255,245,237,0)] px-2 py-2 text-[13px] font-medium leading-[1.3] text-[#7f430e]'>
            {assuranceBadge}
          </p>
        ) : null}

        <div className='mt-5 flex flex-col gap-3'>
          {portfolioValue ? (
            <div className='rounded-2xl border border-solid border-[#ebf2f2] bg-white px-4 py-4'>
              <div className='flex items-center justify-between gap-3'>
                <span className='text-[13px] font-normal leading-[1.3] text-[#3d3d3d]'>
                  {portfolioValueLabel}
                </span>
                <span className='text-[14px] font-medium leading-[1.3] text-[#3d3d3d]'>{portfolioValue}</span>
              </div>
            </div>
          ) : null}

          <div
            className={`rounded-2xl border border-solid border-[#ebf2f2] bg-white px-3 py-1 ${isLoading ? 'animate-pulse opacity-70' : ''}`}
            aria-busy={isLoading}
          >
            <div className='flex items-center justify-between gap-3 px-0 pb-3 pt-3'>
              <span className='text-[13px] font-medium leading-[1.3] text-[#3d3d3d]'>{slabRateLabel}</span>
              <span className='text-right text-[14px] font-medium leading-[1.3] text-[#3d3d3d]'>
                {price}
                <span className='text-[12px] font-normal'>{priceSuffix}</span>
              </span>
            </div>

            <div className='border-t border-solid border-[#ebf2f2]' />

            <div className='flex flex-col gap-3 py-3'>
              <div className='flex items-center justify-between gap-3'>
                <span className='text-[13px] font-normal leading-[1.3] text-[#6a6e6e]'>
                  {slabPortfolioColumnLabel}
                </span>
                <span className='text-[13px] font-normal leading-[1.3] text-[#6a6e6e]'>
                  {slabPriceColumnLabel}
                </span>
              </div>

              <ul className='m-0 flex list-none flex-col gap-1 p-0' aria-label='Elite price by portfolio value'>
                {tiers.map((tier) => {
                  const isActive = tier.id === activeTierId
                  return (
                    <li
                      key={tier.id}
                      aria-current={isActive ? 'true' : undefined}
                      className={isActive ? 'rounded-lg bg-[#fff5ed] py-2.5 pr-2.5' : 'py-[5px]'}
                    >
                      <div className='flex items-center justify-between gap-3'>
                        <span
                          className={`flex min-w-0 flex-1 items-center gap-1.5 text-[13px] leading-[1.3] ${isActive ? 'font-medium text-[#7f430e]' : 'font-normal text-[#3d3d3d]'
                            }`}
                        >
                          {isActive ? (
                            <span
                              aria-hidden='true'
                              className='inline-block h-4 w-0.5 shrink-0 rounded-[20px] bg-[#7f430e]'
                            />
                          ) : null}
                          {tier.portfolioLabel}
                        </span>
                        <span className='flex shrink-0 items-center gap-1.5 text-[13px] leading-[1.3]'>
                          {tier.listPrice ? (
                            <span
                              className={`w-14 text-right line-through ${isActive
                                  ? 'font-normal text-[#7f430e] opacity-50'
                                  : 'font-normal text-[#8e9393]'
                                }`}
                            >
                              {tier.listPrice}
                            </span>
                          ) : null}
                          <span
                            className={
                              isActive
                                ? 'font-medium whitespace-nowrap text-[#7f430e]'
                                : 'font-normal whitespace-nowrap text-[#3d3d3d]'
                            }
                          >
                            {tier.price}
                          </span>
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>

            {slabFooterNote ? (
              <>
                <div className='border-t border-solid border-[#ebf2f2]' />
                <div className='flex items-center justify-center gap-2 pb-3 pt-2'>
                  <img
                    src={GRAPH_ARROW_SRC}
                    alt=''
                    aria-hidden='true'
                    width={14}
                    height={14}
                    className='size-3.5 shrink-0'
                  />
                  <p className='m-0 text-center text-[13px] font-medium leading-[1.3] text-[#cc6e30]'>
                    {slabFooterNote}
                  </p>
                </div>
              </>
            ) : null}
          </div>

          {lockNote ? (
            <div className='rounded-2xl border border-solid border-[#ebf2f2] bg-white px-4 py-3'>
              <Info lineClassName='!bg-[#d1d6d6]' contentClassName='!text-[13px] !leading-[1.4] !text-[#6a6e6e]'>
                {lockNote}
              </Info>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className='sticky bottom-0 bg-white px-5 pt-4'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}
      >
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-xl border-none bg-[#232323] px-6 text-base font-medium text-white'
          onClick={() => {
            trackElitePlansClick(ElitePlansEventId.PricingDetailsClose)
            onClose()
          }}
        >
          Close
        </button>
      </div>
    </div>
  )
}

function ElitePricingDetailsErrorState({
  onRetry,
  onClose
}: {
  onRetry: () => void
  onClose: () => void
}) {
  return (
    <div className='bg-white text-left' role='alert'>
      <div className='px-5 pb-6 pt-8'>
        <WarningIcon className='mb-2' />
        <h2
          id={ELITE_PLANS_PRICING_DETAILS_TITLE_ID}
          tabIndex={-1}
          className='mb-3 text-2xl font-medium leading-[1] text-black outline-none'
        >
          Something went wrong
        </h2>
        <p className='max-w-72 text-[14px] text-grey40'>
          We're trying to figure out this issue. Please wait for a few minutes and try again.
        </p>
      </div>

      <div
        className='flex flex-col gap-3 bg-white px-5 pt-4'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }}
      >
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-2xl border-none bg-grey10 px-5 text-[15px] font-medium text-white'
          onClick={onRetry}
        >
          Retry
        </button>
        <button
          type='button'
          className='ep-focus-ring flex min-h-[56px] w-full items-center justify-center rounded-2xl border border-[#D1D6D6] bg-white px-5 text-[15px] font-medium text-[#232323]'
          onClick={() => {
            trackElitePlansClick(ElitePlansEventId.PricingDetailsClose)
            onClose()
          }}
        >
          Close
        </button>
      </div>
    </div>
  )
}
